import * as d3 from "d3";
import { useEffect, useRef } from "react";

import { design } from "@/config/design";

import { EntrySegment } from "./ClockPieChart";

type ClockPieChartLayoutProps = {
  entrySegments: EntrySegment[];
};

export function ClockPieChartLayout({ entrySegments }: ClockPieChartLayoutProps) {
  const layerRef = useRef<SVGGElement>(null);

  useEffect(() => {
    if (!layerRef.current) return;

    const selection = d3.select(layerRef.current);
    const hourTicks = d3.range(0, 24);
    const hourLabelRadius = design.ClockPieChart.RADIUS + 14;

    // 時間ラベル（0〜23）
    selection
      .selectAll<SVGTextElement, number>("text.hour-label")
      .data(hourTicks)
      .join(
        (enter) =>
          enter
            .append("text")
            .attr("class", "hour-label")
            .attr("text-anchor", "middle")
            .style("font-size", "1em")
            .attr("transform", (d) => {
              const angle = (d / 24) * 2 * Math.PI;
              const x = Math.sin(angle) * hourLabelRadius;
              const y = -Math.cos(angle) * hourLabelRadius;
              return `translate(${x}, ${y})`;
            })
            .text((d) => String(d)),
        (update) =>
          update.attr("transform", (d) => {
            const angle = (d / 24) * 2 * Math.PI;
            const x = Math.sin(angle) * hourLabelRadius;
            const y = -Math.cos(angle) * hourLabelRadius;
            return `translate(${x}, ${y})`;
          }),
        (exit) => exit.remove(),
      );

    // 目盛オーバーレイ（時間・分の目盛線）
    const overlay = selection.selectAll<SVGGElement, unknown>("g.tick-overlay").data([null]);
    const overlayEnter = overlay.enter().append("g").attr("class", "tick-overlay").style("pointer-events", "none");
    const overlayG = overlayEnter.merge(overlay);

    // 時間目盛
    overlayG
      .selectAll<SVGLineElement, number>("line.hour-tick")
      .data(hourTicks)
      .join(
        (enter) =>
          enter
            .append("line")
            .attr("class", "hour-tick")
            .attr("x1", 0)
            .attr("y1", -design.ClockPieChart.RADIUS)
            .attr("x2", 0)
            .attr("y2", -design.ClockPieChart.RADIUS * 0.9)
            .attr("transform", (d) => `rotate(${(d / 24) * 360})`)
            .style("stroke-width", "1px"),
        (update) => update.attr("transform", (d) => `rotate(${(d / 24) * 360})`),
        (exit) => exit.remove(),
      );

    // 5分刻みの目盛
    const minuteTicks = d3.range(0, 24 * 5);
    overlayG
      .selectAll<SVGLineElement, number>("line.minute-tick")
      .data(minuteTicks)
      .join(
        (enter) =>
          enter
            .append("line")
            .attr("class", "minute-tick")
            .attr("x1", 0)
            .attr("y1", -design.ClockPieChart.RADIUS)
            .attr("x2", 0)
            .attr("y2", -design.ClockPieChart.RADIUS * 0.95)
            .attr("transform", (d) => `rotate(${(d / (24 * 5)) * 360})`)
            .style("stroke-width", "0.5px"),
        (update) => update.attr("transform", (d) => `rotate(${(d / (24 * 5)) * 360})`),
        (exit) => exit.remove(),
      );

    // // セグメント境界線
    // const boundaryAngles = Array.from(
    //   new Set(
    //     entrySegments
    //       .flatMap((entrySegment) => [(entrySegment.startHour / 24) * 360, (entrySegment.endHour / 24) * 360])
    //       .map((a) => Math.round(a * 10) / 10),
    //   ),
    // ).sort((a, b) => a - b);
    //
    // selection
    //   .selectAll(".segment-boundary")
    //   .data(boundaryAngles)
    //   .join(
    //     (enter) =>
    //       enter
    //         .append("line")
    //         .attr("class", "segment-boundary")
    //         .attr("x1", 0)
    //         .attr("y1", 0)
    //         .attr("x2", 0)
    //         .attr("y2", -design.ClockPieChart.RADIUS)
    //         .attr("transform", (deg) => `rotate(${deg})`)
    //         .style("stroke-width", "2px")
    //         .style("stroke-linecap", "round")
    //         .style("pointer-events", "none"),
    //     (update) => update.attr("transform", (deg) => `rotate(${deg})`),
    //     (exit) => exit.remove(),
    //   );
  }, [entrySegments]);

  return (
    <g
      ref={layerRef}
      className="layout-layer"
    />
  );
}
